import React, { Dispatch, Reducer, SetStateAction, useCallback, useEffect, useReducer, useState } from 'react';

import { ParsedData, QueryOptions, QueryParams, ResponseMeta, useMutation } from '@betterment/js-runtime';
import * as Sentry from '@sentry/browser';

import { ActionRequestParams, CoPilotResponseMeta, LIMIT } from '../utils/co_pilot';

// @ts-expect-error TS2345 violates strict null checks
export const CoPilotSectionContext = React.createContext<CoPilotSectionContextData<ItemWithID>>(null);

export type Filter = 'primary' | 'secondary';
export interface CoPilotSectionContextData<Item extends ItemWithID> extends CoPilotData<Item> {
  isItemSelected: (id: string) => boolean;
  handleSelectedItemChange: (id: string) => void;
  handleSendReminder: (customItems?: string[]) => void;
  handleIdentifiedDismissals: (ids: Array<string>) => void;
  handleDismissals: () => void;
  loadMore: () => void;
  handleSelectAllChange: () => void;
  handleToggleModal: () => void;
  refresh: () => void;
  selectedItemsCount: number;
  status: Status;
  lastPage: boolean;
  itemList: Item[];
  showSuccessBanner: boolean;
  canSendReminders: boolean;
  hasDismissal: boolean;
  setHasDismissal: Dispatch<SetStateAction<boolean>>;
  handleFilterChange: (newFilters: Filter[]) => void;
}

export type ItemWithID = { id: string };
type CoPilotData<Item extends ItemWithID> = {
  selectedItems: string[];
  itemList: Item[];
  itemsBefore: string;
  offset: number;
  lastPage: boolean;
  showSuccessBanner: boolean;
  filters: Filter[];
};
export type Status =
  | 'empty'
  | 'idle'
  | 'selecting'
  | 'allSelected'
  | 'sendingReminder'
  | 'dismissing'
  | 'modalOpen'
  | 'loading'
  | 'fetchError'
  | 'actionError';
type State<Item extends ItemWithID> = [Status, CoPilotData<Item>];
type Payload<Item extends ItemWithID> = {
  items: Item[];
  meta: { retrievedAt: string; lastPage: boolean };
};
type Action<Item extends ItemWithID> =
  | {
      type: 'TOGGLE_SELECTED_ITEM';
      id: Item['id'];
    }
  | {
      type: 'TOGGLE_ALL';
    }
  | {
      type: 'DISMISS';
    }
  | {
      type: 'DISMISS_DONE';
    }
  | {
      type: 'REMINDER_DONE';
    }
  | {
      type: 'SEND_REMINDER';
    }
  | {
      type: 'TOGGLE_MODAL';
    }
  | {
      type: 'REQUEST_SUCCESSFUL';
      payload: Payload<Item>;
    }
  | {
      type: 'LOAD_MORE';
    }
  | {
      type: 'ERROR';
    }
  | {
      type: 'REFRESH';
    }
  | {
      type: 'FILTER';
      filters: Filter[];
    };

const VALID_SECTION_NAMES: string[] = [
  'failed_acats',
  'pending_acats',
  'missing_beneficiaries',
  'account_lockouts',
  'account_approvals',
  'pending_invites',
  'acats_approvals',
  'rmds',
];

type parameters = ({ seenRecord }) => Promise<ParsedData<{}, ResponseMeta>>;
type updateSeenFnDefinition = (updateSeenFn: parameters) => void;

const updateSeen: updateSeenFnDefinition = updateSeenFn => {
  const sectionName = window.location.pathname.split('co_pilot/')[1];
  let keyPathName: string;

  if (sectionName === 'rmds') {
    keyPathName = 'outstanding_rmds';
  } else if (VALID_SECTION_NAMES.includes(sectionName)) {
    keyPathName = sectionName;
  }

  // @ts-expect-error TS2454 violates strict null checks
  if (keyPathName) {
    void updateSeenFn({ seenRecord: keyPathName });
  }
};

function toggleSelectedItems<Item extends ItemWithID>(
  context: CoPilotData<Item>,
  action: { type: 'TOGGLE_SELECTED_ITEM'; id: Item['id'] }
): string[] {
  return context.itemList.reduce((results, item) => {
    if (item.id === action.id) {
      if (results.includes(item.id)) return results.filter(id => id !== item.id);
      return results.concat(item.id);
    }
    return results;
  }, context.selectedItems);
}

function selectAllItems<Item extends ItemWithID>(context: CoPilotData<Item>): string[] {
  return context.itemList.map(item => item.id);
}

function increaseOffset<Item extends ItemWithID>(context: CoPilotData<Item>): number {
  return context.offset + LIMIT;
}

const emptiedItemList = { offset: 0, lastPage: false, itemsBefore: '', itemList: [], selectedItems: [] };
const reducer: Reducer<State<ItemWithID>, Action<ItemWithID>> = (
  [state, context]: State<ItemWithID>,
  action: Action<ItemWithID>
) => {
  switch (state) {
    case 'idle':
      switch (action.type) {
        case 'TOGGLE_ALL':
          return [
            'allSelected',
            {
              ...context,
              selectedItems: selectAllItems(context),
              showSuccessBanner: false,
            },
          ];
        case 'TOGGLE_SELECTED_ITEM':
          return [
            'selecting',
            {
              ...context,
              selectedItems: toggleSelectedItems(context, action),
              showSuccessBanner: false,
            },
          ];
        case 'LOAD_MORE':
          return ['loading', { ...context, offset: increaseOffset(context), showSuccessBanner: false }];
        case 'FILTER':
          return ['loading', { ...context, ...emptiedItemList, filters: action.filters }];
        case 'REFRESH':
          return [
            'loading',
            {
              ...context,
              ...emptiedItemList,
            },
          ];
        default:
          return [state, context];
      }
    case 'selecting':
      switch (action.type) {
        case 'TOGGLE_ALL':
          return ['allSelected', { ...context, selectedItems: selectAllItems(context) }];
        case 'TOGGLE_SELECTED_ITEM': {
          const selectedItems = toggleSelectedItems(context, action);
          const hasAllSelected = selectedItems.length === context.itemList.length;
          const hasNoneSelected = selectedItems.length === 0;
          if (hasNoneSelected) return ['idle', { ...context, selectedItems }];
          if (hasAllSelected) return ['allSelected', { ...context, selectedItems }];
          return ['selecting', { ...context, selectedItems }];
        }
        case 'SEND_REMINDER':
          return ['sendingReminder', context];
        case 'TOGGLE_MODAL':
          return ['modalOpen', context];
        case 'LOAD_MORE':
          return ['loading', { ...context, offset: increaseOffset(context) }];
        case 'FILTER':
          return ['loading', { ...context, ...emptiedItemList, filters: action.filters }];
        default:
          return [state, context];
      }
    case 'allSelected':
      switch (action.type) {
        case 'TOGGLE_ALL':
          return ['idle', { ...context, selectedItems: [] }];
        case 'TOGGLE_SELECTED_ITEM':
          return ['selecting', { ...context, selectedItems: toggleSelectedItems(context, action) }];
        case 'SEND_REMINDER':
          return ['sendingReminder', context];
        case 'TOGGLE_MODAL':
          return ['modalOpen', context];
        case 'FILTER':
          return ['loading', { ...context, ...emptiedItemList, filters: action.filters }];
        case 'LOAD_MORE':
          return ['loading', { ...context, offset: increaseOffset(context) }];
        default:
          return [state, context];
      }
    case 'sendingReminder':
      switch (action.type) {
        case 'REMINDER_DONE':
          return [
            'loading',
            {
              ...context,
              ...emptiedItemList,
              showSuccessBanner: true,
            },
          ];
        case 'FILTER':
          return ['loading', { ...context, ...emptiedItemList, filters: action.filters }];
        case 'ERROR':
          return ['actionError', context];
        default:
          return [state, context];
      }
    case 'dismissing':
      switch (action.type) {
        case 'DISMISS_DONE':
          return [
            'loading',
            {
              ...context,
              ...emptiedItemList,
            },
          ];
        case 'ERROR':
          return ['actionError', context];
        case 'FILTER':
          return ['loading', { ...context, ...emptiedItemList, filters: action.filters }];
        default:
          return [state, context];
      }
    case 'modalOpen':
      switch (action.type) {
        case 'DISMISS':
          return ['dismissing', context];
        case 'TOGGLE_MODAL': {
          const hasAllSelected = context.selectedItems.length === context.itemList.length;
          if (hasAllSelected) return ['allSelected', context];
          return ['selecting', context];
        }
        default:
          return [state, context];
      }
    case 'loading':
      switch (action.type) {
        case 'REQUEST_SUCCESSFUL': {
          const currentItemList = context.itemList.concat(action.payload.items);
          if (currentItemList.length === 0 || !action.payload.meta)
            return ['empty', { ...context, lastPage: true, selectedItems: [] }];
          const newContext = {
            itemsBefore: action.payload.meta.retrievedAt,
            itemList: currentItemList,
            lastPage: action.payload.meta.lastPage,
          };
          if (context.selectedItems.length) return ['selecting', { ...context, ...newContext }];
          return ['idle', { ...context, ...newContext }];
        }
        case 'ERROR': {
          return ['fetchError', { ...context, lastPage: true, selectedItems: [] }];
        }
        case 'FILTER': {
          return ['loading', { ...context, ...emptiedItemList, filters: action.filters }];
        }
        default:
          return [state, context];
      }
    case 'actionError':
      switch (action.type) {
        case 'TOGGLE_ALL':
          return ['allSelected', { ...context, selectedItems: selectAllItems(context) }];
        case 'TOGGLE_SELECTED_ITEM': {
          const selectedItems = toggleSelectedItems(context, action);
          const hasAllSelected = selectedItems.length === context.itemList.length;
          const hasNoneSelected = selectedItems.length === 0;
          if (hasNoneSelected) return ['idle', { ...context, selectedItems }];
          if (hasAllSelected) return ['allSelected', { ...context, selectedItems }];
          return ['selecting', { ...context, selectedItems }];
        }
        case 'SEND_REMINDER':
          return ['sendingReminder', context];
        case 'TOGGLE_MODAL':
          return ['modalOpen', context];
        case 'LOAD_MORE':
          return ['loading', { ...context, offset: increaseOffset(context) }];
        case 'FILTER':
          return ['loading', { ...context, ...emptiedItemList, filters: action.filters }];
        default:
          return [state, context];
      }
    case 'fetchError':
      switch (action.type) {
        case 'REFRESH':
          return ['loading', context];
        case 'FILTER':
          return ['loading', { ...context, ...emptiedItemList, filters: action.filters }];
        default:
          return [state, context];
      }
    case 'empty':
      switch (action.type) {
        case 'FILTER':
          return ['loading', { ...context, ...emptiedItemList, filters: action.filters }];
        default:
          return [state, context];
      }
    default:
      return [state, context];
  }
};

type Order = {
  col: string;
  dir: string;
};

interface CoPilotSectionProviderProps<Item extends ItemWithID> {
  postSendReminder?: (data?: ActionRequestParams) => Promise<ParsedData<{}, ResponseMeta>>;
  postHandleDismissal: (data?: ActionRequestParams) => Promise<ParsedData<{}, ResponseMeta>>;
  initialOrder: Order[];
  fetch(overrides?: Pick<QueryOptions, 'qs'>): Promise<ParsedData<Item[], CoPilotResponseMeta>>;
}

export const CoPilotSectionProvider: React.FC<React.PropsWithChildren<CoPilotSectionProviderProps<ItemWithID>>> = ({
  fetch, // eslint-disable-line @typescript-eslint/unbound-method
  initialOrder,
  children,
  postHandleDismissal,
  postSendReminder,
}) => {
  const [[state, context], send] = useReducer(reducer, [
    'loading',
    {
      itemList: [],
      selectedItems: [],
      itemsBefore: '',
      lastPage: false,
      offset: 0,
      showSuccessBanner: false,
      filters: ['primary', 'secondary'],
    },
  ]);
  const { selectedItems, itemsBefore, itemList, lastPage, showSuccessBanner, offset, filters } = context;
  const selectedItemsCount = selectedItems.length;

  const [updateLastSeen] = useMutation('/api/v1/co_pilot/last_visits/seen', {
    method: 'post',
  });
  const [hasDismissal, setHasDismissal] = useState(false);

  const fetchItems = useCallback(async () => {
    const {
      data: items,
      meta,
      errors,
    } = await fetch({
      qs: {
        limit: LIMIT,
        offset: context.offset,
        order: JSON.stringify(initialOrder),
        filters: context.filters,
      } as QueryParams,
    });
    if (errors) {
      send({ type: 'ERROR' });
    } else {
      // @ts-expect-error TS2322 violates strict null checks
      send({ type: 'REQUEST_SUCCESSFUL', payload: { meta, items } });
    }
  }, [context, fetch]); // TODO: Make initialOrder stable, e.g extract it as a constant instead of constructing it in the params

  useEffect(() => {
    if (state === 'loading') {
      void fetchItems();
    }
  }, [fetchItems, state]);

  useEffect(() => updateSeen(updateLastSeen), [updateLastSeen]);

  const canSendReminders = !!postSendReminder;
  const values = React.useMemo(() => {
    const handleSendReminder = async (customItems = undefined) => {
      send({ type: 'SEND_REMINDER' });
      let response: ParsedData<{}, ResponseMeta>;

      if (customItems && (customItems as string[]).length > 0) {
        send({ type: 'TOGGLE_SELECTED_ITEM', id: (customItems as string[])[0] });
        // @ts-expect-error TS2722 violates strict null checks
        response = await postSendReminder({ itemIds: customItems as string[], itemsBefore });
      } else {
        // @ts-expect-error TS2722 violates strict null checks
        response = await postSendReminder({ itemIds: selectedItems, itemsBefore });
      }

      if (response.errors) {
        send({ type: 'ERROR' });
      } else {
        setTimeout(() => {
          send({ type: 'REMINDER_DONE' });
        }, 1000);
      }
    };

    const handleIdentifiedDismissals = async (ids: Array<string>) => {
      send({ type: 'DISMISS' });
      const response = await postHandleDismissal({ itemIds: ids, itemsBefore });
      if (response.errors) {
        send({ type: 'ERROR' });
      } else {
        setHasDismissal(true);
        send({ type: 'DISMISS_DONE' });
      }
    };

    const handleDismissals = async () => {
      await handleIdentifiedDismissals(selectedItems);
    };

    const handleToggleModal = () => {
      send({ type: 'TOGGLE_MODAL' });
    };

    const isItemSelected = (id: string) => {
      return selectedItems.some(selectedId => selectedId === id);
    };

    const handleSelectedItemChange = (id: string) => {
      send({ type: 'TOGGLE_SELECTED_ITEM', id });
    };

    const handleSelectAllChange = () => {
      send({ type: 'TOGGLE_ALL' });
    };

    const loadMore = () => {
      send({ type: 'LOAD_MORE' });
    };

    const refresh = () => {
      send({ type: 'REFRESH' });
    };

    const handleFilterChange = (newFilters: Filter[]) => {
      const sortedNewFilters = newFilters.sort();
      const sortedFilters = filters.sort();
      const hasUnchangedFilters =
        sortedFilters.length === sortedNewFilters.length &&
        sortedFilters.every((filter, index) => {
          return sortedNewFilters[index] === filter;
        });
      if (!hasUnchangedFilters) {
        send({ type: 'FILTER', filters: sortedNewFilters });
      }
    };

    return {
      isItemSelected,
      handleSelectedItemChange,
      handleSelectAllChange,
      selectedItemsCount,
      handleSendReminder,
      handleDismissals,
      handleIdentifiedDismissals,
      handleToggleModal,
      selectedItems,
      offset,
      filters,
      itemsBefore,
      itemList,
      status: state,
      lastPage,
      loadMore,
      showSuccessBanner,
      refresh,
      canSendReminders,
      hasDismissal,
      setHasDismissal,
      handleFilterChange,
    };
  }, [
    selectedItemsCount,
    selectedItems,
    offset,
    itemsBefore,
    itemList,
    state,
    lastPage,
    showSuccessBanner,
    canSendReminders,
    hasDismissal,
    setHasDismissal,
    postHandleDismissal,
    postSendReminder,
    filters,
  ]);

  // @ts-expect-error TS2322 violates strict null checks
  return <CoPilotSectionContext.Provider value={values}>{children}</CoPilotSectionContext.Provider>;
};

export function useCoPilotSectionState<Item extends ItemWithID>() {
  const context = React.useContext<CoPilotSectionContextData<Item>>(
    CoPilotSectionContext as unknown as React.Context<CoPilotSectionContextData<Item>>
  );
  if (!context) {
    throw new Error('useCoPilotSectionState must be used under CoPilotSectionProvider');
  }
  return context;
}

export const useRerendCountOnDismiss = (data, refetchCounts: () => Promise<unknown>, loading: boolean) => {
  const { hasDismissal, setHasDismissal } = useCoPilotSectionState<ItemWithID>();
  useEffect(() => {
    if (loading) return;
    if (hasDismissal || data == null) {
      refetchCounts()
        .then(() => {
          setHasDismissal(false);
        })
        .catch(err => {
          Sentry.captureException(err);
        });
    }
  }, [hasDismissal, data, setHasDismissal, refetchCounts, loading]);
};
